import { useState, useCallback } from 'react';
import {
  Folder,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  FolderPlus,
  FileBox,
  Download,
  Trash2,
} from 'lucide-react';
import { useDroppable } from '@dnd-kit/core';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFolderTree, useDeleteFolder } from '@/features/sources/hooks/useFolders';
import type { FolderTreeNode } from '@/features/sources/types';
import type { FolderType } from '@/types/database';

interface FolderTreeProps {
  selectedFolderId: string | null;
  onSelectFolder: (folderId: string | null) => void;
  onCreateFolder?: (parentId: string | null) => void;
}

interface FolderTreeItemProps {
  node: FolderTreeNode;
  depth: number;
  selectedFolderId: string | null;
  expandedIds: Set<string>;
  onSelectFolder: (folderId: string | null) => void;
  onToggleExpand: (folderId: string) => void;
  onDeleteFolder?: (folderId: string) => void;
}

const folderTypeIcon: Record<FolderType, typeof Folder> = {
  regular: Folder,
  result: FileBox,
  import: Download,
};

const folderTypeLabel: Record<FolderType, string> = {
  regular: 'Folder',
  result: 'Results',
  import: 'Import',
};

function FolderTreeItem({
  node,
  depth,
  selectedFolderId,
  expandedIds,
  onSelectFolder,
  onToggleExpand,
  onDeleteFolder,
}: FolderTreeItemProps) {
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedFolderId === node.id;
  const hasChildren = node.children.length > 0;
  const Icon = isExpanded && hasChildren
    ? FolderOpen
    : folderTypeIcon[node.folder_type];

  const { isOver, setNodeRef } = useDroppable({ id: node.id });

  return (
    <div ref={setNodeRef}>
      <button
        type="button"
        onClick={() => onSelectFolder(node.id)}
        className={cn(
          'group flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-sm transition-colors',
          'hover:bg-slate-100',
          isOver && 'bg-indigo-100 ring-2 ring-indigo-300',
          isSelected && !isOver && 'bg-indigo-50 text-indigo-700 font-medium',
          !isSelected && !isOver && 'text-slate-700'
        )}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(node.id);
            }}
            className="flex h-4 w-4 shrink-0 items-center justify-center rounded hover:bg-slate-200"
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ) : (
          <span className="h-4 w-4 shrink-0" />
        )}

        {node.color ? (
          <span
            className="h-4 w-4 shrink-0 rounded-sm"
            style={{ backgroundColor: node.color }}
          />
        ) : (
          <Icon
            className={cn(
              'h-4 w-4 shrink-0',
              isSelected ? 'text-indigo-600' : 'text-slate-400'
            )}
          />
        )}

        <TooltipProvider delayDuration={500}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="truncate">{node.name}</span>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-xs">
              <p className="font-medium">{node.name}</p>
              {node.description && (
                <p className="text-xs text-slate-500">{node.description}</p>
              )}
              <p className="text-xs text-slate-400 mt-1">
                {folderTypeLabel[node.folder_type]} &middot; {node.source_count} source
                {node.source_count !== 1 ? 's' : ''}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {node.source_count > 0 && (
          <Badge
            variant="secondary"
            className={cn(
              'ml-auto h-5 min-w-[20px] justify-center px-1.5 text-xs',
              isSelected && 'bg-indigo-100 text-indigo-700'
            )}
          >
            {node.source_count}
          </Badge>
        )}

        {onDeleteFolder && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteFolder(node.id);
            }}
            className="ml-auto h-5 w-5 shrink-0 items-center justify-center rounded opacity-0 group-hover:opacity-100 hover:bg-rose-100 text-slate-400 hover:text-rose-600 transition-all hidden group-hover:flex"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </button>

      {isExpanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <FolderTreeItem
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedFolderId={selectedFolderId}
              expandedIds={expandedIds}
              onSelectFolder={onSelectFolder}
              onToggleExpand={onToggleExpand}
              onDeleteFolder={onDeleteFolder}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function FolderTree({
  selectedFolderId,
  onSelectFolder,
  onCreateFolder,
}: FolderTreeProps) {
  const { data: tree, isLoading } = useFolderTree();
  const deleteFolder = useDeleteFolder();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const handleToggleExpand = useCallback((folderId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  const handleDeleteFolder = useCallback(
    async (folderId: string) => {
      if (!window.confirm('Delete this folder? Sources inside won\'t be deleted.')) return;
      try {
        await deleteFolder.mutateAsync(folderId);
        if (selectedFolderId === folderId) {
          onSelectFolder(null);
        }
      } catch {
        // Error already handled by the hook's onError
      }
    },
    [deleteFolder, selectedFolderId, onSelectFolder]
  );

  if (isLoading) {
    return (
      <div className="space-y-2 p-2">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-7 animate-pulse rounded-md bg-slate-100"
          />
        ))}
      </div>
    );
  }

  const isEmpty = !tree || tree.length === 0;

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Folders
        </span>
        {onCreateFolder && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onCreateFolder(null)}
          >
            <FolderPlus className="h-4 w-4" />
          </Button>
        )}
      </div>

      <button
        type="button"
        onClick={() => onSelectFolder(null)}
        className={cn(
          'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
          'hover:bg-slate-100',
          selectedFolderId === null && 'bg-indigo-50 text-indigo-700 font-medium',
          selectedFolderId !== null && 'text-slate-700'
        )}
      >
        <Folder className={cn(
          'h-4 w-4',
          selectedFolderId === null ? 'text-indigo-600' : 'text-slate-400'
        )} />
        <span>All Sources</span>
      </button>

      {isEmpty ? (
        <div className="px-3 py-6 text-center">
          <Folder className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-2 text-sm text-slate-500">No folders yet</p>
          {onCreateFolder && (
            <Button
              variant="link"
              size="sm"
              className="mt-1 text-indigo-600"
              onClick={() => onCreateFolder(null)}
            >
              Create your first folder
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-0.5 px-1">
          {tree.map((node) => (
            <FolderTreeItem
              key={node.id}
              node={node}
              depth={0}
              selectedFolderId={selectedFolderId}
              expandedIds={expandedIds}
              onSelectFolder={onSelectFolder}
              onToggleExpand={handleToggleExpand}
              onDeleteFolder={handleDeleteFolder}
            />
          ))}
        </div>
      )}
    </div>
  );
}
